import { apiRequest } from "./apiClient";
import { createActivity, getActivityActor } from "./activityService";
import { bagliHatirlaticilariSil } from "./linkedReminderService";
import { emitRestDataChanged } from "./restEventBus";
import { trackEngagementDataAdded } from "./engagementService";
import { areAndroidHealthFeaturesDisabled } from "../utils/platformFeatures";
import { currentTimeZonePayload } from "../hooks/useUserTimezone";
import { recordMeaningfulReviewAction } from "./appReviewService";

const enc = (v) => encodeURIComponent(String(v || ""));
const HEALTH_LINKED_CALENDAR_CATEGORIES = new Set(["Randevu", "Sağlık", "Appointment", "Health"]);

export const takvimEtkinlikleriGetirService = async (familyId) => {
  if (!familyId) return [];
  // Takvim Android'de desteklenen bir modüldür. Android Lite başlığı backend'de
  // Sağlık/Randevu kategorili takvim kayıtlarını da filtrelememeli.
  const data = await apiRequest(`/calendar/${enc(familyId)}`, { skipAndroidLiteHeader: true });
  return Array.isArray(data?.events) ? data.events : [];
};

export const takvimHariciEtkinlikleriTopluAktarService = async ({ familyId, events = [], tamAd, user }) => {
  if (!familyId || !Array.isArray(events) || !events.length) return { importedCount: 0, duplicateCount: 0 };
  const payload = events.map((event) => ({
    baslik: event.baslik,
    aciklama: event.aciklama || "",
    tarih: event.tarih,
    endDate: event.endDate || event.tarih,
    saat: event.allDay ? "" : (event.saat || ""),
    renk: event.renk || "#64748b",
    externalImportKey: event.externalImportKey,
    externalProvider: event.provider || "device",
    externalCalendarTitle: event.sourceTitle || "",
  }));
  const chunks = [];
  for (let index = 0; index < payload.length; index += 400) chunks.push(payload.slice(index, index + 400));
  let data = { importedCount: 0, duplicateCount: 0, batchId: null };
  for (let index = 0; index < chunks.length; index += 1) {
    const result = await apiRequest(`/calendar/${enc(familyId)}/import-external-batch`, {
      method: "POST",
      body: JSON.stringify({
        events: chunks[index], ekleyen: tamAd, ekleyenUid: user?.uid,
        timeZone: currentTimeZonePayload().timeZone, finalBatch: index === chunks.length - 1,
      }),
    });
    data = {
      importedCount: data.importedCount + Number(result?.importedCount || 0),
      duplicateCount: data.duplicateCount + Number(result?.duplicateCount || 0),
      batchId: result?.batchId || data.batchId,
    };
  }
  emitRestDataChanged({ familyId, resource: "calendar", action: "refresh" });
  return data;
};

export const takvimEtkinlikEkleService = async ({
  familyId,
  baslik,
  aciklama,
  tarih,
  endDate,
  saat,
  kategori,
  ilgiliKisiUid,
  ilgiliKisiAd,
  renk,
  hedefTipi,
  hedefUidler,
  recurrenceType = "none",
  source = null,
  externalImportKey = null,
  externalProvider = null,
  externalCalendarTitle = null,
  tamAd,
  profilAvatar = null,
  user,
}) => {
  const payload = {
    baslik: String(baslik || "").trim(),
    aciklama: String(aciklama || "").trim(),
    tarih,
    endDate: endDate || tarih,
    saat,
    kategori,
    ilgiliKisiUid,
    ilgiliKisiAd,
    renk,
    hedefTipi,
    hedefUidler,
    recurrenceType,
    ...(source ? { source } : {}),
    ...(externalImportKey ? { externalImportKey } : {}),
    ...(externalProvider ? { externalProvider } : {}),
    ...(externalCalendarTitle ? { externalCalendarTitle } : {}),
    ekleyen: tamAd,
    ekleyenUid: user?.uid,
    type: "event",
    createdAt: new Date().toISOString(),
    timeZone: currentTimeZonePayload().timeZone,
  };

  // Android Lite sürümünde sağlık modülü bulunmadığı için Sağlık/Randevu
  // kategorilerinde yalnızca takvim etkinliği oluşturulur. Mevcut platform
  // başlığına eklenen bu açık talep, bağlantılı sağlık kaydı üretimini kapatır.
  const androidHealthSyncDisabled =
    areAndroidHealthFeaturesDisabled() && HEALTH_LINKED_CALENDAR_CATEGORIES.has(String(kategori || "").trim());
  const requestPayload = androidHealthSyncDisabled
    ? { ...payload, skipLinkedHealthRecord: true }
    : payload;

  const data = await apiRequest(`/calendar/${enc(familyId)}`, {
    method: "POST",
    body: JSON.stringify(requestPayload),
  });
  const event = data?.event || payload;
  const events = Array.isArray(data?.events) && data.events.length ? data.events : [event];
  const id = event?.id || event?.firebaseId || data?.id || data?.eventId;
  const eventWithId = id && !event?.id ? { ...event, id } : event;
  trackEngagementDataAdded({ familyId, moduleKey: "calendar", itemId: id });
  void recordMeaningfulReviewAction(user?.uid, {
    trigger: "calendar_created",
    moduleKey: "calendar",
    itemId: id,
  }).catch(() => {});
  emitRestDataChanged({ familyId, resource: "calendar", action: "added", item: eventWithId });
  if (!areAndroidHealthFeaturesDisabled() && eventWithId?.linkedHealthRecordId) {
    emitRestDataChanged({ familyId, resource: "health", action: "refresh" });
  }

  // Takvim kaydı ana işlemdir. Aktivite akışındaki geçici bir hata, başarıyla
  // eklenmiş etkinliği arayüzde başarısızmış gibi göstermemelidir.
  await createActivity({
    familyId,
    type: "calendar_event_added",
    category: "calendar",
    actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
    payload: { eventTitle: payload.baslik, date: tarih, time: saat, category: kategori, person: ilgiliKisiAd },
    targetPage: "takvim",
    targetId: id,
  }).catch((error) => {
    console.warn("[calendarService] Etkinlik eklendi; aktivite kaydı atlandı:", error?.message);
  });

  return { id, event: eventWithId, events };
};

export const takvimEtkinlikSilService = async ({ familyId, eventId, event, scope = "single", user, tamAd, profilAvatar = null }) => {
  await bagliHatirlaticilariSil({
    familyId,
    kaynak: "calendar",
    kaynakId: eventId,
    reminderId: event?.reminderId,
    reminderIds: event?.reminderIds,
  });

  const suffix = scope === "series" ? "?scope=series" : "";
  const data = await apiRequest(`/calendar/${enc(familyId)}/${enc(eventId)}${suffix}`, { method: "DELETE" });
  const deletedEventIds = Array.isArray(data?.deletedEventIds) ? data.deletedEventIds : [eventId];
  if (scope === "series") {
    emitRestDataChanged({ familyId, resource: "calendar", action: "refresh" });
  } else {
    emitRestDataChanged({ familyId, resource: "calendar", action: "deleted", itemId: eventId });
  }
  if (!areAndroidHealthFeaturesDisabled()) {
    emitRestDataChanged({ familyId, resource: "health", action: "refresh" });
  }

  await createActivity({
    familyId,
    type: "calendar_event_deleted",
    category: "calendar",
    actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
    payload: { eventTitle: event?.baslik || "" },
    targetPage: "takvim",
    targetId: eventId,
  });
  return deletedEventIds;
};

export const takvimEtkinlikGuncelleService = async ({
  familyId,
  eventId,
  yeniVeri,
  event,
  user,
  profilAvatar = null,
  tamAd,
}) => {
  const androidHealthSyncDisabled =
    areAndroidHealthFeaturesDisabled() &&
    HEALTH_LINKED_CALENDAR_CATEGORIES.has(String(yeniVeri?.kategori || "").trim());
  const updatePayload = {
    timeZone: currentTimeZonePayload().timeZone,
    ...yeniVeri,
    ...(androidHealthSyncDisabled ? { skipLinkedHealthRecord: true } : {}),
    updatedAt: new Date().toISOString(),
  };
  const data = await apiRequest(`/calendar/${enc(familyId)}/${enc(eventId)}`, {
    method: "PUT",
    body: JSON.stringify(updatePayload),
  });
  const updated = data?.event || { ...event, ...yeniVeri, id: eventId };
  emitRestDataChanged({ familyId, resource: "calendar", action: "updated", item: updated });
  if (!areAndroidHealthFeaturesDisabled()) {
    emitRestDataChanged({ familyId, resource: "health", action: "refresh" });
  }

  await createActivity({
    familyId,
    type: "calendar_event_updated",
    category: "calendar",
    actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
    payload: { eventTitle: yeniVeri?.baslik || event?.baslik || "" },
    targetPage: "takvim",
    targetId: eventId,
  });
};
